import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { RefreshCw, CheckSquare, Square, Check, X, AlertTriangle, Star, Loader2 } from 'lucide-react';
import DataTable from './DataTable';
import { setPreviewExtractionLoading } from '../store/slices/documentsSlice';
import LoadingSpinner from './LoadingSpinner';
import ProgressBar from './ProgressBar';
import { getMatchStatusColors, getDisplayStatusColors } from '../services/colorService';
import { apiFetch } from '../utils/apiUtils';
import { sessionManager } from '../services/api';
import useProgress from '../hooks/useProgress';
import socketService from '../services/socketService';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
`;

const Container = styled.div`
  background: white;
  border-radius: 8px;
  max-width: 1400px;
  width: 100%;
  max-height: 95vh;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  min-height: 0; /* Ensures flex child can shrink */
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid #d1d5db;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f9fafb'};
    border-color: ${props => props.active ? '#2563eb' : '#9ca3af'};
  }
`;

const BottomPanel = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 0 0 8px 8px;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: #3b82f6;
  color: white;
  
  &:hover:not(:disabled) {
    background: #2563eb;
  }
`;

const SpinnerIcon = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

const ApplyOverridesButton = styled(Button)`
  background: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background: #2563eb;
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  }
  
  &:disabled {
    background: #9ca3af;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled(Button)`
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  
  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const DangerButton = styled(Button)`
  background: #dc2626;
  color: white;
  
  &:hover:not(:disabled) {
    background: #b91c1c;
  }
`;

const StatusBadge = styled.div`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;


// Progress bar is now imported from ProgressBar.jsx component

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;


const ExtractionPreview = ({ loanId, onClose, onApplyOverrides, autoStart = true, selectedDocuments = [] }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState('idle'); // 'idle' | 'running' | 'complete' | 'error'
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [selectedOverrides, setSelectedOverrides] = useState({});
  const [filterType, setFilterType] = useState('all');
  const [isSaving, setIsSaving] = useState(false);
  const hasStartedRef = useRef(false);
  
  // Get useProgress hook for session management
  const { getSessionId } = useProgress();
  
  // WebSocket for progress updates - Use existing WebSocket service
  useEffect(() => {
    const handleMessage = (message) => {
      if (!message) return;
      if (message.scope && message.scope !== 'preview') {
        return; // Ignore messages explicitly meant for other scopes
      }

      if (message.type === 'progress_update') {
        const { progress: progressValue, status: statusMessage } = message.payload || {};
        setProgress(progressValue ?? 0);
        setProgressMessage(statusMessage || 'Processing...');
      } else if (message.type === 'progress_complete') {
        setProgress(100);
        setProgressMessage('Complete');
        setStatus('complete');
      }
    };

    socketService.subscribe('progress_update', handleMessage);
    socketService.subscribe('progress_complete', handleMessage);

    // Ensure connection is established (connect() is idempotent)
    try {
      socketService.connect();
    } catch (err) {
      console.error('❌ [ExtractionPreview] Failed to ensure Socket.IO connection:', err);
    }

    return () => {
      socketService.unsubscribe('progress_update', handleMessage);
      socketService.unsubscribe('progress_complete', handleMessage);
    };
  }, []);
  
  // Auto-start extraction when component mounts
  useEffect(() => {
    if (autoStart && !hasStartedRef.current && loanId) {
      hasStartedRef.current = true;
      handleReRun();
    }
  }, [autoStart, loanId]);
  
  // Statistics
  const stats = useMemo(() => {
    const totalFields = comparisonData.length;
    const fieldsWithDifferences = comparisonData.filter(item => item.existing_source !== item.new_source).length;
    const selectedCount = Object.values(selectedOverrides).filter(Boolean).length;
    const notFoundCount = comparisonData.filter(item => 
      (item.existing_source === 'Not Found' || item.existing_source === 'N/A') && 
      (item.new_source === 'Not Found' || item.new_source === 'N/A')
    ).length;
    const newMatchesCount = comparisonData.filter(item => {
      // New matches: where new_source has green background (not 'Not Found' or 'N/A') 
      // and existing_source is anything but green (Not Found, N/A, etc.)
      const hasNewValue = item.new_source && item.new_source !== 'Not Found' && item.new_source !== 'N/A';
      const hasOldValue = item.existing_source && item.existing_source !== 'Not Found' && item.existing_source !== 'N/A';
      return hasNewValue && !hasOldValue;
    }).length;
    
    return {
      totalFields,
      fieldsWithDifferences,
      selectedCount,
      notFoundCount,
      newMatchesCount
    };
  }, [comparisonData, selectedOverrides]);
  
  const handleReRun = async () => {
    setStatus('running');
    setProgress(0);
    setProgressMessage('Starting re-run extraction...');
    setError(null);
    
    // Set preview extraction loading flag to prevent main page refresh
    dispatch(setPreviewExtractionLoading(true));
    
    try {
      // ⏳ Wait for WebSocket connection to be ready
      try {
        await socketService.waitForConnection();
        console.log('✅ [ExtractionPreview] WebSocket connection ready');
      } catch (error) {
        console.warn('⚠️ [ExtractionPreview] WebSocket connection timeout:', error);
      }
      
      // Debug: Log selected documents
      console.log('🔍 [ExtractionPreview] Selected documents:', selectedDocuments);
      console.log('🔍 [ExtractionPreview] Mapped documents:', selectedDocuments.map(doc => ({
        id: doc.id,
        fileType: doc.fileType,
        fileName: doc.fileName || doc.original_filename,
        s3Key: doc.s3Key || doc.s3_source_key,
        s3_textract_key: doc.s3_textract_key
      })));
      
      const socketId = socketService.getSocketId();
      console.log('🔌 [ExtractionPreview] Using socket ID for preview:', socketId);

      // Progress will be updated via WebSocket
      const response = await apiFetch(`/extraction/rerun/${loanId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selected_documents: selectedDocuments.map(doc => ({
            id: doc.id,
            fileType: doc.fileType,
            fileName: doc.fileName || doc.original_filename,
            s3Key: doc.s3Key || doc.s3_source_key,
            s3_textract_key: doc.s3_textract_key
          })),
          socket_id: socketId,
        })
      });
      
      const result = response;
      
      console.log('🔍 [EXTRACTION PREVIEW] Response received:', result);
      console.log('🔍 [EXTRACTION PREVIEW] result.success:', result.success);
      console.log('🔍 [EXTRACTION PREVIEW] result.comparison_data length:', result.comparison_data?.length);
      
      if (!result.success) {
        console.error('🔍 [EXTRACTION PREVIEW] Extraction failed:', result.error);
        throw new Error(result.error || 'Re-run extraction failed');
      }
      
      setComparisonData(result.comparison_data || []);
      
      // No auto-selection - everything is opt-in
      setSelectedOverrides({});
      
      
            setStatus('complete');
      
    } catch (err) {
      setError(err.message);
      setStatus('error');
    } finally {
      // Clear preview extraction loading flag
      dispatch(setPreviewExtractionLoading(false));
    }
  };
  
  const handleSelectAll = () => {
    const allSelected = {};
    comparisonData.forEach(item => {
      // Only select fields that have actual differences
      const hasDifference = item.existing_source !== item.new_source;
      if (hasDifference) {
        allSelected[item.field_name] = true;
      }
    });
    setSelectedOverrides(allSelected);
  };
  
  const handleClearAll = () => {
    setSelectedOverrides({});
  };
  
  const handleSelectChanged = (fieldName, selected) => {
    setSelectedOverrides(prev => ({
      ...prev,
      [fieldName]: selected
    }));
  };
  
  const handleApplyOverrides = async () => {
    if (stats.selectedCount === 0) {
      alert('Please select at least one field to override');
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Filter comparison data to only selected items
      const selectedItems = comparisonData.filter(item => 
        selectedOverrides[item.field_name]
      );
      
      // Call the parent callback
      if (onApplyOverrides) {
        await onApplyOverrides(selectedItems);
      }
    } finally {
      setIsSaving(false);
    }
    
    // Close the preview
    if (onClose) {
      onClose();
    }
  };
  
  // Prepare table data with filtering
  const tableData = useMemo(() => {
    let filteredData = comparisonData;
    
    // Apply filter based on filterType
    switch (filterType) {
      case 'changes':
        filteredData = comparisonData.filter(item => item.existing_source !== item.new_source);
        break;
      case 'notFound':
        filteredData = comparisonData.filter(item => 
          (item.existing_source === 'Not Found' || item.existing_source === 'N/A') && 
          (item.new_source === 'Not Found' || item.new_source === 'N/A')
        );
        break;
      case 'newMatches':
        filteredData = comparisonData.filter(item => {
          const hasNewValue = item.new_source && item.new_source !== 'Not Found' && item.new_source !== 'N/A';
          const hasOldValue = item.existing_source && item.existing_source !== 'Not Found' && item.existing_source !== 'N/A';
          return hasNewValue && !hasOldValue;
        });
        break;
      case 'all':
      default:
        filteredData = comparisonData;
        break;
    }
    
    return filteredData.map(item => ({
      ...item,
      selected: selectedOverrides[item.field_name] || false
    }));
  }, [comparisonData, selectedOverrides, filterType]);
  
  
  
  // Track when status changes
  const statusRef = useRef(status);
  if (statusRef.current !== status) {
    statusRef.current = status;
  }
  
  return (
    <Overlay data-cy="extraction-preview-overlay">
      <Container data-cy="extraction-preview-container">
        <Header>
          <Title>
            <RefreshCw size={20} />
            Re-Run Extraction Preview
          </Title>
          <Controls>
            {status === 'idle' && (
              <PrimaryButton onClick={handleReRun}>
                <RefreshCw size={16} />
                Re-Run Extraction
              </PrimaryButton>
            )}
            <SecondaryButton onClick={onClose}>
              Close
            </SecondaryButton>
          </Controls>
        </Header>
        
        <Content>
          {error && (
            <ErrorMessage>
              <AlertTriangle size={16} />
              {error}
            </ErrorMessage>
          )}
          
          {status === 'running' && (
            <div 
              style={{ 
                marginBottom: '1rem',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '1rem'
              }}
              data-cy="modal-extraction-progress"
            >
              <div style={{ 
                textAlign: 'center', 
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: '#3b82f6'
              }}>
                🔄 EXTRACTION IN PROGRESS ({progress}%)
              </div>
              <ProgressBar 
                progress={progress} 
                status={progressMessage || 'Processing documents...'} 
                data-cy="modal-progress-bar-component"
              />
            </div>
          )}
          
          {status === 'complete' && stats.totalFields > 0 && (
            <>
              <FilterButtons>
                <FilterButton 
                  active={filterType === 'changes'} 
                  onClick={() => setFilterType('changes')}
                  data-cy="filter-changes-only"
                >
                  Changes Only ({stats.fieldsWithDifferences})
                </FilterButton>
                <FilterButton 
                  active={filterType === 'notFound'} 
                  onClick={() => setFilterType('notFound')}
                  data-cy="filter-not-found"
                >
                  Not Found ({stats.notFoundCount})
                </FilterButton>
                <FilterButton 
                  active={filterType === 'newMatches'} 
                  onClick={() => setFilterType('newMatches')}
                  data-cy="filter-new-matches"
                >
                  New Matches ({stats.newMatchesCount})
                </FilterButton>
                <FilterButton 
                  active={filterType === 'all'} 
                  onClick={() => setFilterType('all')}
                  data-cy="filter-all"
                >
                  All ({stats.totalFields})
                </FilterButton>
              </FilterButtons>
            <DataTable
            data={tableData}
            data-cy="extraction-comparison-table"
            columns={[
              {
                field: 'field_name',
                header: 'Field',
                width: 'minmax(150px, 1fr)'
              },
              {
                field: 'existing_core',
                header: 'Core',
                width: 'minmax(150px, 1fr)'
              },
              {
                field: 'existing_source',
                header: 'Value',
                width: 'minmax(150px, 1fr)',
                render: (row) => {
                  const matchStatus = row.existing_source_match || 'NO_MATCH';
                  // Check if it's display format (lowercase) or database format (uppercase)
                  const isDisplayFormat = matchStatus === matchStatus.toLowerCase();
                  const colors = isDisplayFormat 
                    ? getDisplayStatusColors(matchStatus) 
                    : getMatchStatusColors(matchStatus);
                  const isSelected = row.selected;
                  
                  return (
                    <div style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#f3f4f6' : colors.backgroundColor,
                      color: isSelected ? '#9ca3af' : colors.textColor,
                      width: '100%',
                      textAlign: 'left',
                      boxSizing: 'border-box',
                      opacity: isSelected ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      position: 'relative'
                    }}>
                      {row.existing_source}
                    </div>
                  );
                }
              },
              {
                field: 'new_source',
                header: 'Proposed',
                width: 'minmax(150px, 1fr)',
                render: (row) => {
                  const matchStatus = row.new_source_match || 'NO_MATCH';
                  // Check if it's display format (lowercase) or database format (uppercase)
                  const isDisplayFormat = matchStatus === matchStatus.toLowerCase();
                  const colors = isDisplayFormat 
                    ? getDisplayStatusColors(matchStatus) 
                    : getMatchStatusColors(matchStatus);
                  const isSelected = row.selected;
                  
                  return (
                    <div style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#dcfce7' : colors.backgroundColor,
                      color: isSelected ? '#065f46' : colors.textColor, // Dark green text for selected items
                      width: '100%',
                      textAlign: 'left',
                      boxSizing: 'border-box',
                      border: isSelected ? '2px solid #10b981' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      {row.new_source}
                      {isSelected && (
                        <Star 
                          size={14} 
                          style={{
                            color: '#10b981',
                            fill: '#10b981'
                          }}
                        />
                      )}
                    </div>
                  );
                }
              },
              {
                field: 'selected',
                header: 'Select',
                width: '80px',
                render: (row) => {
                  // Only show checkbox if there are actual differences to override
                  const hasDifference = row.existing_source !== row.new_source;
                  
                  if (!hasDifference) {
                    return <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>—</span>;
                  }
                  
                  return (
                    <div 
                      data-cy={`select-field-${row.field_name}`}
                      data-selected={row.selected ? 'true' : 'false'}
                      style={{ 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => handleSelectChanged(row.field_name, !row.selected)}
                    >
                      {row.selected ? (
                        <CheckSquare size={20} style={{ color: '#3b82f6' }} data-cy="icon-selected" />
                      ) : (
                        <Square size={20} style={{ color: '#9ca3af' }} data-cy="icon-unselected" />
                      )}
                    </div>
                  );
                }
              }
            ]}
            rowClassName={(row) => 
              row.has_changes ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''
            }
          />
            </>
          )}
          
          {status === 'complete' && stats.totalFields === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              No comparison data available. Try running the extraction again.
            </div>
          )}
        </Content>
        
        {status === 'complete' && stats.totalFields > 0 && (
          <BottomPanel>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <SecondaryButton onClick={handleSelectAll} data-cy="select-all-button">
                <CheckSquare size={16} />
                Select All
              </SecondaryButton>
              <SecondaryButton onClick={handleClearAll} data-cy="clear-all-button">
                <Square size={16} />
                Clear All
              </SecondaryButton>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }} data-cy="rerun-selected-count">
                {stats.selectedCount} selected
              </span>
              <ApplyOverridesButton 
                onClick={handleApplyOverrides}
                disabled={stats.selectedCount === 0 || isSaving}
                data-cy="save-rerun-results"
              >
                {isSaving ? (
                  <>
                    <SpinnerIcon size={16} />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </ApplyOverridesButton>
            </div>
          </BottomPanel>
        )}
      </Container>
    </Overlay>
  );
};

export default ExtractionPreview;
